/**
 * Pure presence transforms for the WS adapter. `buildPresenceSnapshot` yields the consumer
 * shape `{ connectedUsers, imagePresence, genPresence, gradingLocks, cropLocks }`; the viewer
 * `pfpUrl` is a storage key — pass it to `<StorageImage storageKey>`, not as a raw URL.
 */

/**
 * Decode one `VisionPresenceUpdated` payload into a presence entry. Empty-string ids map to
 * null (proto3 has no absent-scalar; empty string IS "no value", matching the sibling event
 * arms). `kind` is left as the raw enum value for the adapter to branch on.
 */
export function presenceFromProto(value) {
  return {
    accountId: value.accountId,
    name: value.name || '',
    pfpUrl: value.pfpUrl || null,
    kind: value.kind,
    imageId: value.imageId || null,
    generationId: value.generationId || null,
    gradingLock: value.gradingLockGenerationId || null,
    cropLock: value.cropLockGenerationId || null,
  };
}

/**
 * Fold the account→entry map into the consumer snapshot: connected users deduped, per-image
 * and per-generation viewers deduped, and locks first-writer-wins (the first holder seen for a
 * generation id wins; Map iteration is insertion order, so it tracks announce order).
 */
export function buildPresenceSnapshot(stateMap) {
  const connectedUsers = [];
  const imagePresence = {};
  const genPresence = {};
  const gradingLocks = {};
  const cropLocks = {};
  const seenUsers = new Set();

  for (const entry of stateMap.values()) {
    if (!entry.accountId) continue;

    const viewer = {
      userId: entry.accountId,
      displayName: entry.name,
      pfpUrl: entry.pfpUrl,
    };

    if (!seenUsers.has(entry.accountId)) {
      seenUsers.add(entry.accountId);
      connectedUsers.push({ ...viewer, imageId: entry.imageId || null });
    }

    if (entry.imageId) {
      if (!imagePresence[entry.imageId]) imagePresence[entry.imageId] = [];
      if (!imagePresence[entry.imageId].some((v) => v.userId === entry.accountId)) {
        imagePresence[entry.imageId].push(viewer);
      }
    }

    if (entry.generationId) {
      if (!genPresence[entry.generationId]) genPresence[entry.generationId] = [];
      if (!genPresence[entry.generationId].some((v) => v.userId === entry.accountId)) {
        genPresence[entry.generationId].push(viewer);
      }
    }

    if (entry.gradingLock && !gradingLocks[entry.gradingLock]) {
      gradingLocks[entry.gradingLock] = viewer;
    }

    if (entry.cropLock && !cropLocks[entry.cropLock]) {
      cropLocks[entry.cropLock] = viewer;
    }
  }

  return { connectedUsers, imagePresence, genPresence, gradingLocks, cropLocks };
}
